import { debug } from '../log';
import { toJSON } from '../platform/json';
import { concatUrl } from '../universal';
import { AppSetting } from 'dependencies/appsettings';
import { authProvider } from 'dependencies/auth';
import { HttpStatusCodes } from './constants';
import { BlobCache } from 'app/infrastructure/remote-data/blob-cache';

const defaultHttpOptions = {
  method: 'GET'
};

const defaultHttpHeaders = {
  Accept: 'application/json; charset=UTF-8',
  'content-type': 'application/json; charset=UTF-8'
};

/**
 * Specific error info for http request errors
 *
 * @export
 * @class RemoteDataError
 * @extends {Error}
 */

export interface RemoteResult {
  message: string;
  response: any;
  userMessages?: any;
  validationErrors?: any;
}

/**
 * The legacy reponse model includes body messages to the user for non-200 reponses.
 * This function deconstructs the response
 *
 * @param message
 * @param response
 */
export function remoteDataError(message: string, response: any) {
  let result: RemoteResult = { message, response };

  return response.json().then((body: any) => {
    if (body.messages && body.messages.userMessages) {
      result = { ...result, userMessages: body.messages.userMessages };
    }
    if (body.data && body.data.validationErrors) {
      result = { ...result, validationErrors: body.data.validationErrors };
    }
    return result;
  }).catch(() => result);
}

/**
 * Universal JSON data fetch
 *
 * @export
 * @param {string} url
 * @param {string} [httpMethod]
 * @returns {Promise<any>}
 */
export async function fetchJson(
  url: string,
  processResponse: (response: Response) => Promise<Response>,
  options: HttpOptions): Promise<any> {

  const request = http(url, processResponse, options);

  return request.then(r => {
    if (r.status === HttpStatusCodes.NO_CONTENT || r.status === HttpStatusCodes.NOT_MODIFIED) {
      return null;
    }
    return r.json();
  });
}

/**
 * Universal text data fetch
 *
 * @export
 * @param {string} url
 * @param {string} [httpMethod]
 * @returns {Promise<string>}
 */
export async function fetchText(
  url: string,
  processResponse: (response: Response) => Promise<Response>,
  options: HttpOptions): Promise<string> {

  const request = http(url, processResponse, options, checkStatusWithRedirect);

  return request.then(r => {
    if (r.status === HttpStatusCodes.NO_CONTENT || r.status === HttpStatusCodes.NOT_MODIFIED) {
      return null;
    }
    return r?.text?.();
  });
}

export interface HttpOptions {
  body?: any;
  headers?: any;
  method?: string;
  referenceToken?: string;
  importance?: string;
}

export function http(
  url: string,
  processResponse: (response: Response) => Promise<Response>,
  options: HttpOptions = {},
  checkStatus: (response: Response) => Promise<Response> = defaultCheckStatus): Promise<Response> {

  debug('http', 'requesting', url, options);

  const fetchOptions = Object.assign({}, defaultHttpOptions, options);
  fetchOptions.body = toJSON(fetchOptions.body);
  fetchOptions.headers = {
    ...defaultHttpHeaders,
    ...options.headers
  };

  debug('http options', fetchOptions);

  if (!fetchOptions.method || !fetchOptions.headers) {
    throw new Error('method and headers must be defined for fetch');
  }

  return fetch(url, fetchOptions).then(processResponse).then(checkStatus);
}

const defaultCheckStatus = async (response: Response) => {
  if (response.status >= HttpStatusCodes.OK && response.status < 300 || response.status === HttpStatusCodes.NOT_MODIFIED) {
    return response;
  } else {
    if (response.status === HttpStatusCodes.UNAUTHORIZED) {
      return Promise.reject(response);
    }
    const err = await remoteDataError(response.statusText, response);
    return Promise.reject(err);
  }
};

export const checkStatusWithRedirect = async (response: Response) => {
  if (response.status >= HttpStatusCodes.OK && response.status < 300 || response.status === HttpStatusCodes.NOT_MODIFIED
    || response.status === HttpStatusCodes.SEE_OTHER) {
    return response;
  } else {
    if (response.status === HttpStatusCodes.UNAUTHORIZED) {
      return Promise.reject(response);
    }
    const err = await remoteDataError(response.statusText, response);
    return Promise.reject(err);
  }
};

/**
 * Returns a native `blob` response from a GET request
 *
 * @param url a relative URL. The current API base url will be prepended before the request is made.
 */
const attachmentCache = new BlobCache(25);

export const fetchAttachment = (url: string) => async (id: number, thumbnail: boolean): Promise<Blob> => {
  // TODO: kill this and make caller use RemoteData instead
  const baseUrl = AppSetting.ApiBaseUrl;
  const thumbnailUrl = thumbnail ? '/thumbnail' : undefined;
  const blobUrl = concatUrl(baseUrl, url, id, thumbnailUrl);

  const cached = attachmentCache.get(blobUrl);
  if (cached) {
    return cached;
  }

  const authHeaders = authProvider.headers;
  const headers = new Headers();
  authHeaders && Object.keys(authHeaders).forEach(h => headers.append(h, authHeaders[h]));
  const response = await fetch(blobUrl, { headers });
  const blob = await response.blob();
  attachmentCache.add(blobUrl, blob);

  return blob;
};
/**
 * Creates a async cancellable XMLHttpRequest for a POST request
 *
 * @param url a relative URL. The current API base url will be prepended before the request is made.
 */
export const createUploader = (url: string, method: string = 'POST', baseUrl: string = AppSetting.ApiBaseUrl): XMLHttpRequest => {
  // TODO: inject deps from a higher level or abstract the function to a higher level module.
  const authHeaders = authProvider.headers;
  const xhr = new XMLHttpRequest();
  xhr.open(method, concatUrl(baseUrl, url));
  authHeaders && Object.keys(authHeaders).forEach(h => xhr.setRequestHeader(h, authHeaders[h]));
  return xhr;
};
